import React, { useState } from 'react'
import JSZip from 'jszip'
import { saveAs } from 'file-saver'

/**
 * ExportDocsZip
 * props:
 *  - items: Array<{ title, type, communityCode, communityName, from?, file?, createdAt?, notes? }>
 *  - filename?: string
 *  - onDone?: () => void
 */
export default function ExportDocsZip({ items = [], filename = 'community-documents.zip', onDone }) {
  const [busy, setBusy] = useState(false)

  async function fetchAsBlob(url) {
    try {
      const res = await fetch(url)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return await res.blob()
    } catch (e) {
      return new Blob([`Download failed: ${url}\n${e?.message || ''}`], { type: 'text/plain' })
    }
  }

  function esc(s) {
    return String(s || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;')
  }

  function fmt(ts) {
    if (!ts) return '—'
    if (ts?.seconds) return new Date(ts.seconds * 1000).toLocaleString()
    const d = new Date(ts)
    return isNaN(d) ? '—' : d.toLocaleString()
  }

  async function doExport() {
    if (!items?.length || busy) return
    setBusy(true)
    const zip = new JSZip()
    const rows = []

    for (const it of items) {
      const hasFile = it?.file?.downloadURL
      let storedName = ''
      if (hasFile) {
        // Use the exact stored file name, no numbering
        storedName = String(it?.file?.name || 'document').replace(/[^\w.\- ]+/g, '_')
        const blob = await fetchAsBlob(it.file.downloadURL)
        zip.file(`files/${storedName}`, blob)
      }

      rows.push(`
        <tr>
          <td>${esc(it.title || '—')}</td>
          <td>${esc(it.source === 'CAF' ? 'CAF' : (it.type || '—'))}</td>
          <td>${esc(it.communityName || it.communityCode || '—')}</td>
          <td>${esc(it.from || (it.source === 'CAF' ? 'CAF' : 'Community'))}</td>
          <td>${esc(fmt(it.createdAt))}</td>
          <td>${esc((Array.isArray(it.tags) ? it.tags.join(', ') : '') || '—')}</td>
          <td>${esc(it.notes || '—')}</td>
          <td>${storedName ? `<a href="./files/${encodeURIComponent(storedName)}" download>${esc(storedName)}</a>` : '—'}</td>
        </tr>
      `)
    }

    const html = `<!doctype html>
<html>
<head>
<meta charset="utf-8">
<title>Community Documents Export</title>
<style>
  body { font-family: system-ui, -apple-system, Segoe UI, Roboto, sans-serif; margin: 24px; }
  h1 { margin: 0 0 12px; font-size: 20px; }
  table { border-collapse: collapse; width: 100%; }
  th, td { border: 1px solid #ddd; padding: 8px 10px; vertical-align: top; }
  th { background: #f7f7f7; text-align: left; }
  tr:nth-child(even) td { background: #fafafa; }
</style>
</head>
<body>
  <h1>Community Documents Export</h1>
  <p>Generated: ${esc(new Date().toLocaleString())}</p>
  <table>
    <thead>
      <tr>
        <th>Title</th>
        <th>Type</th>
        <th>Community</th>
        <th>From</th>
        <th>Created</th>
        <th>Tags</th>
        <th>Notes</th>
        <th>File</th>
      </tr>
    </thead>
    <tbody>
      ${rows.join('\n')}
    </tbody>
  </table>
</body>
</html>`

    zip.file('index.html', html)

    try {
      const blob = await zip.generateAsync({ type: 'blob' })
      saveAs(blob, filename)
    } finally {
      setBusy(false)
      if (typeof onDone === 'function') onDone()
    }
  }

  return (
    <button className="secondary" onClick={doExport} disabled={!items?.length || busy}>
      {busy ? 'Preparing…' : `Export ${items?.length || 0} Docs (.zip)`}
    </button>
  )
}